/**
 * The product: a map, four sliders, and a place you tapped.
 *
 * What this file does is wiring, and the shape of the wiring is the whole
 * argument of `docs/frontend.md`. A slider move goes to the shader as four
 * floats and to the panel as one arithmetic call -- no fetch, no upload, no
 * recompute -- because `propmap prod` baked the expensive half. A tap resolves
 * its area out of a raster that arrived with the first paint. The only things
 * that ever wait on the network are the ones a reader asked for by name: a
 * category's evidence points, and the detail behind a dot.
 *
 * The tuning client in `web/tuning/` is a different application with a
 * different audience, a build of its own and no deployment, and it stays
 * exactly as it is.
 */

import { Marker, Popup } from "maplibre-gl";

import { AdPolicy, detectAdBlocker } from "./ads.ts";
import { Areas } from "./area.ts";
import { Chrome } from "./chrome.ts";
import { Data } from "./data.ts";
import {
  Destinations, addDestinationLayers, destinationAt, hoverCursor, showDestinations,
} from "./destinations.ts";
import { clearSlot, configuredUnit, fillSlot } from "./gpt.ts";
import { Evidence, showsLines } from "./points.ts";
import { asHundred, count, escapeHtml, metres, money, walkMinutes } from "./format.ts";
import type { LayerSpec, Manifest } from "./manifest.ts";
import { assertLittleEndian, loadArray, loadManifest } from "./manifest.ts";
import { rightmoveUrl, streetViewUrl, zooplaUrl } from "./links.ts";
import { createMap } from "./map.ts";
import {
  countAdBlocked, countAdClosed, countAdShown, countAdUnfilled, countAreaOffMap, countBasemapFallback,
  countContextLost, countDetailOpened, countOutbound, countSharedLink, countStartFailure,
  countPreset, countStreetZoom, countTapOutside, installClientMetrics, noteInteraction, notePaint,
  onInteraction,
} from "./metrics.ts";
import { mix, sampleBaked, samplePresence } from "./mixer.ts";
import type { LayerRead } from "./mixer.ts";
import { renderAreaPanel } from "./panel.ts";
import type { PanelRow } from "./panel.ts";
import { PRESETS, weightsOf } from "./presets.ts";
import { Projector } from "./project.ts";
import { buildRamp } from "./ramp.ts";
import { BlendLayer } from "./raster.ts";
import type { LoadedLayer } from "./raster.ts";
import { SOURCE_NOTES } from "./sources.ts";
import { Store, fromQuery } from "./state.ts";
import type { State } from "./state.ts";
import "maplibre-gl/dist/maplibre-gl.css";
import "./style.css";

const NARROW = 760;

/** Where the crime bubbles have split into points: the reader is looking at streets. */
const STREET_ZOOM = 15;

/** Where the reader last tapped, which is what the panel's numbers describe. */
interface Tap {
  lon: number;
  lat: number;
}

async function start(): Promise<void> {
  assertLittleEndian();
  installClientMetrics();
  if (window.location.search.length > 1) countSharedLink();
  const root = document.getElementById("app")!;
  const manifest = await loadManifest();

  const data = new Data(manifest);
  const store = new Store(manifest, fromQuery(manifest, window.location.search));
  const travelLayer = manifest.layers.find((layer) => layer.destinations) ?? null;
  const destinations = travelLayer ? new Destinations(travelLayer) : null;
  // wired once the map exists; until then the picker has nothing to show on
  let setPicking = (_open: boolean): void => {};

  const chrome = new Chrome(root, manifest, {
    onWeight: (name, value) => store.update({ weights: { [name]: value } }),
    onSelect: (name) => store.update({ view: store.state.view === name ? null : name }),
    onPhase: (phase) => store.update({ phase }),
    onCloseArea: () => store.update({ area: null }),
    onToggleDetail: () => store.update({ detail: !store.state.detail }),
    onInfo: (name) => {
      // Not in the store, and so not in the address: "what is this figure?" is
      // a question asked and answered where the reader is standing, not a thing
      // a shared link should carry. The panel is redrawn from here instead.
      openInfo = openInfo === name ? null : name;
      void refreshPanel(store.state);
    },
    onPreset: (name) => {
      const preset = PRESETS.find((p) => p.name === name);
      if (preset) countPreset(preset.name);
      if (preset) store.update({ weights: weightsOf(preset, manifest) });
    },
    onCloseAd: () => closeAd(),
    onPicker: (open) => setPicking(open),
    onResetDestination: () => {
      store.update({ destination: null });
      setPicking(false);
    },
  });
  chrome.render(store.state);

  // The slot fills on an action like any other, so the rule is stated once and
  // in actions rather than in seconds: an ad interrupts somebody who is using
  // the map, never somebody who has left it open on a desk.
  const ads = new AdPolicy();
  // Asked once, long before the first slot can fill: that takes eight actions.
  let adBlocked = false;
  void detectAdBlocker().then((blocked) => { adBlocked = blocked; });
  const closeAd = (): void => {
    if (!ads.isOpen) return;
    ads.dismiss();
    clearSlot();
    chrome.setAd(false);
    countAdClosed();
  };
  const adUnit = configuredUnit();
  onInteraction(() => {
    if (!ads.note()) return;
    // chrome.setAd(true, adBlocked);
    countAdShown();
    if (adBlocked) {
      countAdBlocked();
      return;
    }
    if (adUnit === null) return;
    void fillSlot(chrome.adBody, adUnit).then((fill) => {
      if (!ads.isOpen) return;
      if (fill === "blocked") {
        // the script was refused where the bait was not: a blocker all the same
        adBlocked = true;
        chrome.setAd(true, true);
        countAdBlocked();
      } else if (fill === "empty") {
        // nothing to show is no reason to show a box
        ads.dismiss();
        clearSlot();
        chrome.setAd(false);
        countAdUnfilled();
      }
    });
  });

  // -- the first paint ----------------------------------------------------
  const phaseLayer = manifest.layers.find((layer) => layer.phases !== null) ?? null;
  const [lattice, cell, loaded, phases] = await Promise.all([
    loadArray(manifest.lattice.osgb_km) as Promise<Float32Array>,
    loadArray(manifest.areas.cell) as Promise<Uint16Array>,
    loadLayers(manifest, store.state),
    loadPhases(phaseLayer),
  ]);

  const projector = new Projector(manifest.lattice, lattice);
  const areas = new Areas(manifest, projector, cell, data);
  const { map, source, detail } = await createMap(chrome.mapContainer, manifest,
                                                  window.innerWidth > NARROW);
  chrome.setStatus(statusLine(manifest, source, detail));
  if (source === "offline") countBasemapFallback();
  map.getCanvas().addEventListener("webglcontextlost", () => countContextLost());

  const raster = new BlendLayer(loaded, manifest.blend, manifest.lattice, lattice,
                               buildRamp(manifest.blend.colour));
  // under the basemap's own labels, so street names stay readable through it
  const labels = map.getStyle().layers?.find((layer) => layer.type === "symbol");
  map.addLayer(raster, labels?.id);

  const evidence = new Evidence(map, manifest, data);
  addSelectionLayers(map);
  if (travelLayer) {
    addDestinationLayers(map, travelLayer.colour);
    hoverCursor(map);
  }
  data.prefetch();
  notePaint();

  // Crossing into street level is the reader going from choosing an area to
  // inspecting one; zooming about once there is not counted again.
  let atStreet = map.getZoom() >= STREET_ZOOM;
  map.on("zoomend", () => {
    const now = map.getZoom() >= STREET_ZOOM;
    if (now && !atStreet) countStreetZoom();
    atStreet = now;
  });

  // The popup's links, counted by where they lead. A middle click opens a tab
  // without a click event, so it is heard as an auxclick -- which a right click
  // fires too, on its way to a context menu that opens nothing.
  const outbound = (event: MouseEvent): void => {
    if (event.type === "auxclick" && event.button !== 1) return;
    const link = (event.target as HTMLElement | null)?.closest?.("a[data-out]");
    if (link instanceof HTMLElement) countOutbound(link.dataset["out"]!);
  };
  document.addEventListener("click", outbound);
  document.addEventListener("auxclick", outbound);

  // Dev only, and statically dropped from a production build: the headless
  // checks in the scratchpad drive the real map rather than guessing at pixel
  // positions, which is how they can click an actual crime dot or station.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>)["__propmap"] = { map, manifest, store };
  }

  // -- what the reader is looking at --------------------------------------
  let tap: Tap | null = null;

  // The clicked point: a dot, and a popup of links out to that spot.
  const dotEl = document.createElement("div");
  dotEl.className = "place-dot";
  const placeDot = new Marker({ element: dotEl });
  const placePopup = new Popup({ closeButton: true, closeOnClick: false,
                                 maxWidth: "240px", offset: 12 });
  const showPlace = (lon: number, lat: number): void => {
    const at = { lat, lon };
    const link = (href: string, label: string, out: string): string =>
      `<a class="pop-link" href="${escapeHtml(href)}" target="_blank" rel="noopener" ` +
      `data-out="${out}">${label}</a>`;
    placeDot.setLngLat([lon, lat]).addTo(map);
    placePopup.setLngLat([lon, lat])
      .setHTML(`<div class="pop"><div class="pop-links">` +
               link(streetViewUrl(at), "Street View", "streetview") +
               link(rightmoveUrl(at), "Rightmove", "rightmove") +
               link(zooplaUrl(at), "Zoopla", "zoopla") +
               `</div></div>`)
      .addTo(map);
  };
  const clearPlace = (): void => {
    placePopup.remove();
    placeDot.remove();
  };

  // -- where transport is measured to -------------------------------------
  const travelIndex = travelLayer ? manifest.layers.indexOf(travelLayer) : -1;
  // what `loadLayers` fetched for it: the default, whatever the link asked for
  const homeBytes = travelIndex >= 0 ? loaded[travelIndex]!.bytes : null;
  let picking = false;
  // the latest pick wins: a slow raster arriving after a newer choice is dropped
  let destinationToken = 0;

  const drawPicker = async (): Promise<void> => {
    if (!destinations) return;
    const places = picking ? await destinations.list() : null;
    showDestinations(map, picking ? places : null, store.state.destination);
  };

  setPicking = (open: boolean): void => {
    if (!destinations || picking === open) return;
    picking = open;
    chrome.setPicker(open);
    // The transport view's own stations are the same colour as the menu and
    // answer a tap with their own popup, so they step aside while choosing.
    void evidence.show(open ? null : store.state.view, store.state.phase);
    void drawPicker();
  };

  const applyDestination = async (key: string | null): Promise<void> => {
    if (!destinations || !homeBytes) return;
    const token = ++destinationToken;
    if (key === null) {
      chrome.setDestination(destinations.defaultLabel, true);
      raster.setBytes(travelIndex, homeBytes);
      void refreshPanel(store.state);
      return;
    }
    const place = await destinations.find(key).catch(() => null);
    if (token !== destinationToken) return;
    if (!place) {
      // a link to somewhere no longer on the menu opens on the default
      store.update({ destination: null });
      return;
    }
    chrome.setDestination(place.label, false);
    try {
      const bytes = await destinations.raster(place);
      if (token !== destinationToken) return;
      raster.setBytes(travelIndex, bytes);
      void refreshPanel(store.state);
    } catch (err) {
      console.error(err);
      if (token !== destinationToken) return;
      chrome.setStatus(`Journey times to ${place.label} could not be loaded`);
      store.update({ destination: null });
    }
  };

  const weightsFor = (state: State): number[] =>
    manifest.layers.map((layer) =>
      state.view !== null && state.view !== layer.name ? 0 : state.weights[layer.name] ?? 0);

  const apply = (state: State, previous: State | null): void => {
    if (previous) reportInteractions(state, previous);
    if (!state.area) clearPlace();
    raster.setWeights(weightsFor(state));
    if (!previous || previous.view !== state.view) {
      const active = manifest.layers.find((layer) => layer.name === state.view);
      raster.setRamp(buildRamp(active?.colour ?? manifest.blend.colour));
      void evidence.show(state.view, state.phase);
    }
    if (previous && previous.phase !== state.phase && phaseLayer) {
      const bytes = phases[state.phase];
      const index = manifest.layers.indexOf(phaseLayer);
      if (bytes && index >= 0) raster.setBytes(index, bytes);
      void evidence.show(state.view, state.phase);
    }
    if (previous ? previous.destination !== state.destination : state.destination !== null) {
      void applyDestination(state.destination);
      if (picking) void drawPicker();
    }
    chrome.render(state);
    void refreshPanel(state);
  };

  // -- the panel ----------------------------------------------------------
  // A slider moves the blended score and nothing else in the panel, so what
  // depends only on the place is kept between refreshes: the outline drawn on
  // the map, and the detail lines with their nearest-school and station search.
  let drawn: { area: string | null; outline: GeoJSON.Feature | null } = { area: null, outline: null };
  let detailCache: { key: string; details: Record<string, string> } | null = null;
  // The area last checked for being off the map, so that a slider moved with
  // the panel open does not count the same area again.
  let counted: string | null = null;
  /** Which row's `i` is open, or null. One at a time: the panel is narrow. */
  let openInfo: string | null = null;

  const refreshPanel = async (state: State): Promise<void> => {
    if (!state.area) {
      chrome.setPanel(null);
      if (drawn.area !== null) setSelection(map, null);
      drawn = { area: null, outline: null };
      counted = null;
      return;
    }
    await areas.ready();
    const index = areas.indexOf(state.area);
    if (index === null) {
      chrome.setPanel(null);
      return;
    }
    if (drawn.area !== state.area) {
      drawn = { area: state.area, outline: areas.outlineFor(index) };
      setSelection(map, drawn.outline);
    }
    const outline = drawn.outline;
    const at = tap ?? centroidOf(outline);
    if (!at) return;
    const point = projector.toOsgb(at.lon, at.lat);
    const row = areas.rowFor(index);

    const reads: LayerRead[] = [];
    const rows: PanelRow[] = manifest.layers.map((layer) => {
      const weight = state.view !== null && state.view !== layer.name
        ? 0 : state.weights[layer.name] ?? 0;
      const loadedLayer = loaded[manifest.layers.indexOf(layer)]!;
      const sample = point
        ? sampleBaked(loadedLayer.bytes, layer.grid, point.x, point.y, manifest.blend.sampling)
        : { value: NaN, coverage: 0 };
      const presence = point && loadedLayer.presence
        ? samplePresence(loadedLayer.presence, layer.grid, point.x, point.y,
                         manifest.blend.sampling)
        : 1;
      reads.push({
        name: layer.name, weight, activation: layer.activation, k: layer.k,
        value: sample.value, coverage: sample.coverage, presence,
        outsideLabel: layer.presence?.label ?? "",
      });
      return {
        name: layer.name,
        title: layer.title,
        colour: layer.colour,
        score: asHundred(sample.value),
        detail: "",
        weighted: weight > 0,
        viewing: state.view === layer.name,
        notes: SOURCE_NOTES[layer.name] ?? [],
        info: openInfo === layer.name,
      };
    });

    const mixed = mix(reads, manifest.blend);
    if (counted !== state.area) {
      counted = state.area;
      if (!mixed.painted) countAreaOffMap();
    }
    // A place with no blend either sits outside a layer's footprint, or was cut
    // by the cutoff and can name the categories that did it. The third case --
    // a category with no answer here at all -- says nothing: there is no box,
    // because a reader who tapped a park does not need to be told the park is
    // missing from a table.
    const blocked = mixed.outside ?? (mixed.ruledOutBy.length
      ? `Excluded because of: ${mixed.ruledOutBy.map((n) => nameOf(manifest, n)).join(", ")}`
      : null);

    const render = (details: Record<string, string> | null): void => {
      chrome.setPanel(renderAreaPanel({
        name: row?.name ?? state.area!,
        blended: mixed.painted ? asHundred(mixed.value) : null,
        blockedBy: blocked,
        rows: details ? rows.map((r) => ({ ...r, detail: details[r.name] ?? "" })) : rows,
        detail: state.detail,
        pending: row === null,
      }));
    };

    const detailKey = `${state.area}|${at.lon},${at.lat}|${state.phase}`;
    if (state.detail && row && detailCache?.key === detailKey) {
      render(detailCache.details);
      return;
    }
    render(null);

    if (!state.detail || !row) return;
    // The detail lines need the two lists the panel does not: filled in when
    // they arrive, which after the idle prefetch is immediately.
    const [schools, stations] = await Promise.all([data.schools(), data.stations()]);
    const nearestSchool = schools
      ? areas.nearestSchool(schools, at.lon, at.lat, state.phase) : null;
    const nearestStation = stations
      ? areas.nearestStation(stations, at.lon, at.lat) : null;
    const london = manifest.areas.london_median_price;

    const details: Record<string, string> = {
      price: row.medianPrice
        ? `Median sold price, ${escapeHtml(row.msoaName ?? "this neighbourhood")}: ` +
          `<b>${money(row.medianPrice)}</b> · London ${money(london)}`
        : "No ONS median price published for this neighbourhood",
      crime: row.crimeDecile
        ? `Safety decile <b>${row.crimeDecile} of 10</b> nationally ` +
          `(1 is the most crime-affected tenth of England, 10 the safest)` +
          (row.offences ? ` · ${count(row.offences)} offences recorded in 12 months (Met)` : "")
        : "No deprivation rank published for this area",
      transport: nearestStation
        ? `Nearest station: <b>${escapeHtml(nearestStation.name)}</b>` +
          (nearestStation.lines && showsLines(nearestStation.mode)
            ? ` (${escapeHtml(nearestStation.lines)})` : "") +
          ` · ${metres(nearestStation.metres)}, about ` +
          `${walkMinutes(nearestStation.metres)} min walk` +
          (row.connectivity ? ` · DfT connectivity ${Math.round(row.connectivity)}/100` : "")
        : "No station list loaded",
      schools: nearestSchool
        ? `Closest ${nearestSchool.letter === "S" ? "secondary" : "primary"}: ` +
          `<b>${escapeHtml(nearestSchool.name)}</b>` +
          (nearestSchool.grade ? ` — Ofsted: ${escapeHtml(nearestSchool.grade)}` : " — no grade") +
          ` · ${metres(nearestSchool.metres)}`
        : "No school list loaded",
    };
    detailCache = { key: detailKey, details };
    render(details);
  };

  store.subscribe((state, previous) => apply(state, previous));

  map.on("click", (event) => {
    // While choosing a destination the map is the menu: a tap on a dot picks
    // it, and a tap anywhere else does nothing rather than open an area.
    if (picking) {
      const key = destinationAt(map, event.point);
      if (key) {
        store.update({ destination: key });
        setPicking(false);
      }
      return;
    }
    // a marker answers a click with its own popup, not the area under it
    if (evidence.hitsMarker(event.point)) {
      placePopup.remove();
      noteInteraction("evidence");
      return;
    }
    const hit = areas.resolveAt(event.lngLat.lng, event.lngLat.lat);
    if (!hit) {
      countTapOutside();
      tap = null;
      store.update({ area: null });
      clearPlace();
      return;
    }
    tap = { lon: event.lngLat.lng, lat: event.lngLat.lat };
    showPlace(tap.lon, tap.lat);
    void areas.ready().then(() => {
      const resolved = areas.resolveAt(event.lngLat.lng, event.lngLat.lat);
      store.update({ area: resolved?.code ?? null });
      // a tap on the area that is already open is still a tap on a new point
      if (resolved?.code === store.state.area) void refreshPanel(store.state);
    });
  });

  apply(store.state, null);
}

/**
 * What changed, as an action.
 *
 * Read off the state rather than reported at each control, so that a change
 * arriving from a link, a keyboard or a future control counts the same as one
 * from a tap, and no new control can forget to say it happened. A dragged
 * slider fires this continuously and `noteInteraction` coalesces it back into
 * the one gesture it was.
 */
function reportInteractions(state: State, previous: State): void {
  for (const name of Object.keys(state.weights)) {
    if (state.weights[name] !== previous.weights[name]) {
      noteInteraction("weight", Date.now(), name);
      break;
    }
  }
  if (state.view !== previous.view && state.view !== null) noteInteraction("view");
  if (state.phase !== previous.phase) noteInteraction("phase");
  if (state.area !== previous.area && state.area !== null) noteInteraction("area");
  if (state.detail && !previous.detail) countDetailOpened();
}

function nameOf(manifest: Manifest, layer: string): string {
  return manifest.layers.find((l) => l.name === layer)?.title ?? layer;
}

function statusLine(manifest: Manifest, source: string, detail: string): string {
  const notes: string[] = [];
  if (source === "offline") notes.push(`basemap unavailable (${detail})`);
  if (!manifest.railways) notes.push("tube and rail lines unavailable in this build");
  return notes.join(" · ");
}

async function loadLayers(manifest: Manifest, state: State): Promise<LoadedLayer[]> {
  return Promise.all(manifest.layers.map(async (spec) => {
    const asset = spec.phases ? spec.phases.assets[state.phase] ?? spec.score : spec.score;
    const [bytes, presence] = await Promise.all([
      loadArray(asset) as Promise<Uint8Array>,
      spec.presence ? (loadArray(spec.presence.asset) as Promise<Uint8Array>) : null,
    ]);
    return { spec, bytes, presence };
  }));
}

/**
 * Every schools phase, up front.
 *
 * Three baked rasters at 250 m are 60 KB compressed between them, so changing
 * which schools count is a texture upload rather than a request -- and the
 * reader who tries all three pays once.
 */
async function loadPhases(layer: LayerSpec | null): Promise<Record<string, Uint8Array>> {
  if (!layer?.phases) return {};
  const entries = await Promise.all(Object.entries(layer.phases.assets).map(
    async ([key, asset]) => [key, (await loadArray(asset)) as Uint8Array] as const));
  return Object.fromEntries(entries);
}

// ---------------------------------------------------------------------------
// the selected area's outline
// ---------------------------------------------------------------------------

const SELECTION = "propmap-selection";
const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

function addSelectionLayers(map: ReturnType<typeof Object>): void {
  const m = map as import("maplibre-gl").Map;
  m.addSource(SELECTION, { type: "geojson", data: EMPTY });
  m.addLayer({
    id: `${SELECTION}-fill`,
    type: "fill",
    source: SELECTION,
    paint: { "fill-color": "#1c1b18", "fill-opacity": 0.07 },
  });
  m.addLayer({
    id: `${SELECTION}-line`,
    type: "line",
    source: SELECTION,
    layout: { "line-join": "round" },
    paint: { "line-color": "#2b2a26", "line-width": 2.5 },
  });
}

function setSelection(map: import("maplibre-gl").Map, feature: GeoJSON.Feature | null): void {
  const source = map.getSource(SELECTION) as import("maplibre-gl").GeoJSONSource | undefined;
  source?.setData(feature ? { type: "FeatureCollection", features: [feature] } : EMPTY);
}

/** A point inside an area, for a link that named one without naming a point. */
function centroidOf(feature: GeoJSON.Feature | null): Tap | null {
  if (!feature) return null;
  const geometry = feature.geometry;
  const rings = geometry.type === "Polygon"
    ? geometry.coordinates
    : geometry.type === "MultiPolygon" ? geometry.coordinates.flat() : [];
  const ring = rings[0];
  if (!ring?.length) return null;
  let lon = 0;
  let lat = 0;
  for (const point of ring) {
    lon += point[0]!;
    lat += point[1]!;
  }
  return { lon: lon / ring.length, lat: lat / ring.length };
}

void start().catch((err: unknown) => {
  console.error(err);
  countStartFailure();
  const root = document.getElementById("app");
  if (root) {
    root.innerHTML = `<p style="padding:24px;font:14px system-ui;color:#1c1b18">` +
      `This map could not start: ${escapeHtml(String(err))}</p>`;
  }
});
